<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { onKeyStroke } from '@vueuse/core';
import { useAppDownloadStore } from '@/stores/app-download';
import { APP_DOWNLOAD_LINKS } from '@/utils/apostatudo/app-download-deep-link';

// Modal "Baixe o aplicativo" (ENG-495). Mesma casca da roletinha e do Promocode:
// Teleport pro body, bottom-sheet colado no rodapé, slide-up por @keyframes (o
// reset global de transition em main.scss mata `transition`) e a store controla
// a abertura sem navegar de rota.
//
// O CONTEÚDO da folha mora em `Card.vue` desde o ENG-843, para a parede de fim
// do PWA reaproveitar o mesmo cartaz. Aqui ficou só a casca: overlay, backdrop,
// animação de entrada e o botão de fechar — que é passado por slot pra continuar
// dentro do contexto de `cqw` da folha.
const store = useAppDownloadStore();
const { isOpen } = storeToRefs(store);

// `visible` mantém o overlay montado até o slide-down terminar; `sheetOpen`
// dispara a animação.
const visible = ref(false);
const sheetOpen = ref(false);

useBodyScrollLock(visible);

const TEXTS = {
  title: 'Baixe o aplicativo oficial e fique por dentro de todas as novidades!',
  close: 'Fechar',
} as const;

function close() {
  store.close();
}

function onClosed() {
  visible.value = false;
}

watch(
  isOpen,
  (open) => {
    if (open) {
      visible.value = true;
      nextTick(() => {
        sheetOpen.value = true;
      });
    } else {
      sheetOpen.value = false;
    }
  },
  { immediate: true },
);

onKeyStroke('Escape', () => {
  if (isOpen.value) close();
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="ad-overlay"
      @dragstart.prevent
    >
      <div
        class="ad-backdrop"
        :class="{ 'ad-backdrop--out': !sheetOpen }"
        @click="close"
      />

      <Transition
        appear
        name="ad-slide-up"
        @after-leave="onClosed"
      >
        <AppDownloadCard
          v-if="sheetOpen"
          :aria-label="TEXTS.title"
          aria-modal="true"
          :links="APP_DOWNLOAD_LINKS.genericModal"
          role="dialog"
          :title="TEXTS.title"
        >
          <template #close>
            <button
              :aria-label="TEXTS.close"
              class="ad-close"
              type="button"
              @click="close"
            >
              <NuxtIcon name="close" />
            </button>
          </template>
        </AppDownloadCard>
      </Transition>
    </div>
  </Teleport>
</template>

<style>
.ad-slide-up-enter-active {
  animation: ad-slide-up 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.ad-slide-up-leave-active {
  animation: ad-slide-up 0.24s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes ad-slide-up {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<style scoped lang="scss">
// Entre o Promocode (z-9997) e a roletinha (z-9999) — nunca aparecem juntos.
.ad-overlay {
  @apply fixed inset-0 z-[9998] flex flex-col justify-end select-none;

  // Desktop: cartão no centro da tela (como no Figma), não colado no rodapé.
  @media (min-width: 1024px) {
    justify-content: center;
  }
}

.ad-backdrop {
  @apply fixed inset-0;

  background: rgba(0, 0, 0, 0.72);
  animation: ad-fade-in 0.25s ease forwards;
}

.ad-backdrop--out {
  animation: ad-fade-in 0.24s ease reverse forwards;
}

@keyframes ad-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// Fica no slot `close` do Card, ou seja DENTRO da folha — por isso as medidas
// seguem em `cqw` e continuam resolvendo contra o container de lá.
.ad-close {
  @apply absolute flex items-center justify-center rounded-full text-white;

  top: 0;
  right: 4.6cqw;
  width: 7.6cqw;
  height: 7.6cqw;
  font-size: 2.6cqw;
  background: rgba(255, 255, 255, 0.05);

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
}
</style>
